<template>
  <div>
    <div class="c4-sec">
      <h4>{{ tx("set.flow") }}</h4>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.mode") }}</b></div>
        <div class="c4-seg" role="radiogroup" :aria-label="tx('set.mode')">
          <button type="button" role="radio" :aria-checked="s.mode === 'auto'" data-fk="s-mode-auto" @click="set('mode', 'auto')">{{ tx("set.mode_auto") }}</button>
          <button type="button" role="radio" :aria-checked="s.mode === 'manual'" data-fk="s-mode-manual" @click="set('mode', 'manual')">{{ tx("set.mode_manual") }}</button>
        </div>
      </div>
      <div v-if="s.mode === 'auto'" class="c4-row">
        <div class="c4-row-l"><b>{{ tx("set.dur") }}</b><span>{{ tx("set.dur_sub") }}</span></div>
        <div class="c4-stp">
          <button type="button" data-fk="m-dur" :disabled="s.autoDuration <= 2" :aria-label="'−'" @click="set('autoDuration', s.autoDuration - 1)"><i class="mdi mdi-minus" aria-hidden="true"></i></button>
          <output>{{ s.autoDuration }} s</output>
          <button type="button" data-fk="p-dur" :disabled="s.autoDuration >= 20" :aria-label="'+'" @click="set('autoDuration', s.autoDuration + 1)"><i class="mdi mdi-plus" aria-hidden="true"></i></button>
        </div>
      </div>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.traffic") }}</b></div>
        <div class="c4-seg" role="radiogroup" :aria-label="tx('set.traffic')">
          <button v-for="o in ['none', 'one', 'three']" :key="o" type="button" role="radio" :aria-checked="s.typeTraffic === o" :data-fk="'s-traffic-' + o" @click="set('typeTraffic', o)">{{ tx("set.traffic_" + o) }}</button>
        </div>
      </div>
      <div class="c4-row">
        <div class="c4-row-l">
          <b>{{ tx("set.confirm") }}</b>
          <span>{{ confirmAuto ? tx("set.confirm_auto") : tx("set.confirm_manual") }} · {{ tx("set.confirm_sub") }}</span>
        </div>
      </div>
    </div>

    <div class="c4-sec">
      <h4>{{ tx("set.display") }}</h4>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.scale") }}</b></div>
        <div class="c4-seg" role="radiogroup" :aria-label="tx('set.scale')">
          <button v-for="p in presets" :key="p.key" type="button" role="radio" :aria-checked="scale === p.key" :data-fk="'s-scale-' + p.key" @click="$emit('preset', p.key)">{{ p.key.toUpperCase() }}</button>
        </div>
      </div>
      <div class="c4-row">
        <div class="c4-row-l"><b>{{ tx("set.upper") }}</b></div>
        <button type="button" class="c4-sw" role="switch" :aria-checked="s.font.uppercase" :aria-label="tx('set.upper')" data-fk="w-upper" @click="set('font.uppercase', !s.font.uppercase)"></button>
      </div>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.keep") }}</b><span>{{ tx("set.keep_sub") }}</span></div>
        <div class="c4-seg" role="radiogroup" :aria-label="tx('set.keep')">
          <button v-for="n in keepOptions" :key="n" type="button" role="radio" :aria-checked="s.colInRow === n" :data-fk="'s-keep-' + n" @click="set('colInRow', n)">{{ n }}</button>
        </div>
      </div>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.order") }}</b><span>{{ tx("set.order_sub") }}</span></div>
        <div class="c4-seg" role="radiogroup" :aria-label="tx('set.order')">
          <button type="button" role="radio" :aria-checked="s.order === 'oldest'" data-fk="s-order-oldest" @click="set('order', 'oldest')">{{ tx("set.order_oldest") }}</button>
          <button type="button" role="radio" :aria-checked="s.order === 'newest'" data-fk="s-order-newest" @click="set('order', 'newest')">{{ tx("set.order_newest") }}</button>
        </div>
      </div>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.clear") }}</b></div>
        <div class="c4-seg" role="radiogroup" :aria-label="tx('set.clear')">
          <button v-for="o in clearOptions" :key="o.v" type="button" role="radio" :aria-checked="Number(s.clearPreviousOrdersTime) === o.v" :data-fk="'s-clear-' + o.v" @click="set('clearPreviousOrdersTime', o.v)">{{ o.t }}</button>
        </div>
      </div>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.adv") }}</b><span>{{ tx("set.adv_sub") }}</span></div>
        <div class="c4-adv">
          <label>{{ tx("set.adv_user") }}
            <input class="c4-fld" type="number" min="12" max="72" data-fk="adv-user" :value="s.font.size.user" @change="setNum('font.size.user', $event, 12, 72)" />
          </label>
          <label>{{ tx("set.adv_food") }}
            <input class="c4-fld" type="number" min="12" max="72" data-fk="adv-food" :value="s.font.size.food" @change="setNum('font.size.food', $event, 12, 72)" />
          </label>
          <label>{{ tx("set.adv_img") }}
            <input class="c4-fld" type="number" min="40" max="240" data-fk="adv-img" :value="s.image.size" @change="setNum('image.size', $event, 40, 240)" />
          </label>
        </div>
      </div>
    </div>

    <div class="c4-sec">
      <h4>{{ tx("set.date") }}</h4>
      <div class="c4-row">
        <div class="c4-row-l"><b>{{ tx("set.today") }}</b><span>{{ tx("set.today_sub") }}</span></div>
        <button type="button" class="c4-sw" role="switch" :aria-checked="s.takeCurrentDate" :aria-label="tx('set.today')" data-fk="w-today" @click="set('takeCurrentDate', !s.takeCurrentDate)"></button>
      </div>
      <div v-if="!s.takeCurrentDate" class="c4-row col">
        <input class="c4-fld" type="date" data-fk="date" :value="date" :aria-label="tx('set.today')" @change="$emit('date', $event.target.value)" />
      </div>
      <div v-if="languages.length > 1" class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.lang") }}</b></div>
        <select class="c4-fld" data-fk="lang" :value="s.language" :aria-label="tx('set.lang')" @change="set('language', $event.target.value)">
          <option v-for="l in languages" :key="l.code" :value="l.code">{{ l.name }}</option>
        </select>
      </div>
    </div>

    <div class="c4-sec">
      <h4>{{ tx("set.device") }}</h4>
      <div class="c4-row">
        <div class="c4-row-l"><b>{{ tx("set.sound") }}</b></div>
        <button type="button" class="c4-sw" role="switch" :aria-checked="s.sound" :aria-label="tx('set.sound')" data-fk="w-sound" @click="set('sound', !s.sound)"></button>
      </div>
      <div class="c4-row col">
        <div class="c4-row-l"><b>{{ tx("set.camera") }}</b></div>
        <div class="c4-seg" role="radiogroup" :aria-label="tx('set.camera')">
          <button type="button" role="radio" :aria-checked="s.camera === 'on'" data-fk="s-camera-on" @click="set('camera', 'on')">{{ tx("set.camera_on") }}</button>
          <button type="button" role="radio" :aria-checked="s.camera === 'off'" data-fk="s-camera-off" @click="set('camera', 'off')">{{ tx("set.camera_off") }}</button>
        </div>
      </div>
      <div v-if="wakeSupported" class="c4-row">
        <div class="c4-row-l"><b>{{ tx("set.awake") }}</b></div>
        <button type="button" class="c4-sw" role="switch" :aria-checked="s.awake" :aria-label="tx('set.awake')" data-fk="w-awake" @click="set('awake', !s.awake)"></button>
      </div>
      <div class="c4-row">
        <div class="c4-row-l"><b>{{ tx("set.info") }}</b><span>{{ tx("set.info_sub") }}</span></div>
        <button type="button" class="c4-sw" role="switch" :aria-checked="s.infoBox" :aria-label="tx('set.info')" data-fk="w-info" @click="set('infoBox', !s.infoBox)"></button>
      </div>
    </div>
  </div>
</template>

<script>
import canteenV4Text from "@/mixins/canteenV4Text";

export default {
  name: "CanteenSettingsSheet",
  mixins: [canteenV4Text],
  props: {
    settings: { type: Object, required: true },
    // veličina prikaza: s | m | l | xl, ili "" kad su brojevi ručno podešeni
    scale: { type: String, default: "m" },
    presets: { type: Array, default: () => [] },
    languages: { type: Array, default: () => [] },
    confirmAuto: { type: Boolean, default: true },
    date: { type: String, default: "" },
    wakeSupported: { type: Boolean, default: false },
  },
  data() {
    return { keepOptions: [1, 2, 3, 4, 6] };
  },
  computed: {
    s() {
      return this.settings;
    },
    clearOptions() {
      const base = [
        { v: 30, t: "30 s" },
        { v: 60, t: "1 min" },
        { v: 120, t: "2 min" },
        { v: 300, t: "5 min" },
      ];
      const cur = Number(this.s.clearPreviousOrdersTime);
      if (cur && !base.some((o) => o.v === cur)) base.push({ v: cur, t: `${cur} s` });
      return base.sort((a, b) => a.v - b.v);
    },
  },
  methods: {
    set(path, value) {
      this.$emit("change", { path, value });
    },
    setNum(path, e, min, max) {
      const n = Math.round(Number(e.target.value));
      if (!n) {
        e.target.value = path.endsWith("user") ? this.s.font.size.user : path.endsWith("food") ? this.s.font.size.food : this.s.image.size;
        return;
      }
      this.set(path, Math.min(max, Math.max(min, n)));
    },
  },
};
</script>
